import { useEffect, useRef, useState } from "react";
import { Link2, Plus, Trash2, ArrowUp, ArrowDown, RefreshCw, Sparkles, Check, X, EyeOff, Search, AlertTriangle, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const LOCATIONS = [
  { value: "main", label: "Within Main Content" },
  { value: "related_links", label: "Related Links Section" },
  { value: "related_products", label: "Related Products Section" },
  { value: "related_categories", label: "Related Categories Section" },
  { value: "related_brands", label: "Related Brands Section" },
  { value: "related_articles", label: "Related Guides Section" },
  { value: "footer", label: "Footer of Content" },
];
const TARGET_TYPES = ["product", "category", "brand", "page"];
const uid = () => Math.random().toString(36).slice(2, 10);

const statusBadge = (res) => {
  if (!res) return null;
  const map = {
    ok: { cls: "border-green-500/40 text-green-300", icon: CheckCircle2, label: "OK" },
    unpublished: { cls: "border-amber-500/40 text-amber-300", icon: AlertTriangle, label: "Unpublished" },
    broken: { cls: "border-red-500/40 text-red-300", icon: AlertTriangle, label: "Broken" },
  };
  const m = map[res.status] || map.broken;
  const Icon = m.icon;
  return <Badge variant="outline" className={`text-[10px] gap-0.5 ${m.cls}`}><Icon className="h-2.5 w-2.5" />{m.label}</Badge>;
};

export default function InternalLinkManager({ entityType, entityId, links = [], onChange }) {
  const [addType, setAddType] = useState("product");
  const [q, setQ] = useState("");
  const [targets, setTargets] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolved, setResolved] = useState({});
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSug, setLoadingSug] = useState(false);
  const [dismissed, setDismissed] = useState([]);
  const [showDismissed, setShowDismissed] = useState(false);
  const searchTimer = useRef(null);
  const resolveTimer = useRef(null);

  const setLinks = (l) => onChange(l);

  // resolve statuses (debounced)
  useEffect(() => {
    if (resolveTimer.current) clearTimeout(resolveTimer.current);
    resolveTimer.current = setTimeout(() => {
      if (!links.length) { setResolved({}); return; }
      api.post("/admin/seo/resolve-links", { seo: { internal_links: links } })
        .then(({ data }) => {
          const map = {};
          (data.links || []).forEach((r) => { map[r.id] = r; });
          setResolved(map);
        }).catch(() => {});
    }, 500);
    return () => resolveTimer.current && clearTimeout(resolveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(links)]);

  const loadSuggestions = (refresh = false) => {
    if (!entityId) return;
    setLoadingSug(true);
    api.get("/admin/seo/link-suggestions", { params: { entity_type: entityType, entity_id: entityId, refresh } })
      .then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => {}).finally(() => setLoadingSug(false));
    api.get("/admin/seo/link-dismissed", { params: { entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => setDismissed(data.dismissed || [])).catch(() => {});
  };
  useEffect(() => { loadSuggestions(false); /* eslint-disable-next-line */ }, [entityId, entityType]);

  const doSearch = (val) => {
    setQ(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearching(true);
      api.get("/admin/seo/link-targets", { params: { type: addType, q: val } })
        .then(({ data }) => setTargets(data.targets || [])).catch(() => {}).finally(() => setSearching(false));
    }, 350);
  };
  useEffect(() => { if (q) doSearch(q); /* eslint-disable-next-line */ }, [addType]);

  const addTarget = (t) => {
    if (links.find((l) => l.target_type === t.target_type && l.target_id === t.target_id)) {
      toast.error("Already linked."); return;
    }
    setLinks([...links, { id: uid(), target_type: t.target_type, target_id: t.target_id, slug: t.slug,
      url: t.url, anchor: t.anchor, description: "", location: "related_links", order: links.length,
      enabled: true, source: "manual", created_at: new Date().toISOString() }]);
    setQ(""); setTargets([]);
  };

  const upd = (i, patch) => setLinks(links.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= links.length) return;
    const copy = [...links];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setLinks(copy.map((l, k) => ({ ...l, order: k })));
  };

  const acceptSuggestion = async (s) => {
    try {
      const { data } = await api.post("/admin/seo/link-suggestions/act", {
        action: "accept", entity_type: entityType, entity_id: entityId,
        target_type: s.target_type, target_id: s.target_id, anchor: s.anchor, location: "related_links",
      });
      if (data.link) {
        setLinks([...links, { ...data.link, order: links.length }]);
        setSuggestions((sg) => sg.filter((x) => !(x.target_type === s.target_type && x.target_id === s.target_id)));
        toast.success("Link added — remember to save the page.");
      }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const actSuggestion = async (s, action) => {
    try {
      await api.post("/admin/seo/link-suggestions/act", { action, entity_type: entityType, entity_id: entityId, target_type: s.target_type, target_id: s.target_id });
      setSuggestions((sg) => sg.filter((x) => !(x.target_type === s.target_type && x.target_id === s.target_id)));
      if (action === "dismiss") loadSuggestions(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const restore = async (d) => {
    try {
      await api.post("/admin/seo/link-suggestions/act", { action: "restore", entity_type: entityType, entity_id: entityId, target_type: d.target_type, target_id: d.target_id });
      loadSuggestions(true);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="internal-link-manager">
      <div>
        <Label className="text-sm font-medium text-white/85 flex items-center gap-2"><Link2 className="h-4 w-4" /> Internal Links</Label>
        <p className="text-xs text-white/50 mb-3">Link to related content. Links use a stable reference so they stay valid if a slug changes (301 preserved).</p>
      </div>

      {/* Add link (entity picker) */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={addType} onChange={(e) => setAddType(e.target.value)} className="cf-select bg-black/30 border border-white/15 rounded-md px-2 py-2 text-sm" data-testid="link-add-type">
            {TARGET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40" />
            <Input value={q} onChange={(e) => doSearch(e.target.value)} placeholder={`Search ${addType}s to link…`} className="pl-8 bg-black/30 border-white/15" data-testid="link-search" />
          </div>
        </div>
        {(searching || targets.length > 0) && q && (
          <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-white/10 divide-y divide-white/5" data-testid="link-target-results">
            {searching ? <p className="p-2 text-xs text-white/40">Searching…</p> :
              targets.length === 0 ? <p className="p-2 text-xs text-white/40">No matches.</p> :
                targets.map((t) => (
                  <button key={t.target_id} type="button" onClick={() => addTarget(t)} className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm flex items-center justify-between" data-testid={`link-target-${t.target_id}`}>
                    <span className="text-white/85">{t.name}</span><Plus className="h-3.5 w-3.5 text-gold" />
                  </button>
                ))}
          </div>
        )}
      </div>

      {/* Existing links */}
      <div className="space-y-2">
        {links.map((l, i) => {
          const res = resolved[l.id]?.resolved || resolved[l.id];
          return (
            <div key={l.id || i} className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2" data-testid={`internal-link-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] border-white/20 text-white/60">{l.target_type}</Badge>
                <span className="text-sm text-white/85 flex-1 min-w-[120px] truncate">{res?.name || l.slug || l.target_id}</span>
                {statusBadge(res)}
                {l.source === "suggested" && <Badge variant="outline" className="text-[10px] border-gold/40 text-gold"><Sparkles className="h-2.5 w-2.5 mr-0.5" />suggested</Badge>}
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => move(i, -1)} className="text-white/40 hover:text-white p-1"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} className="text-white/40 hover:text-white p-1"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <Switch checked={l.enabled !== false} onCheckedChange={(v) => upd(i, { enabled: v })} data-testid={`link-enable-${i}`} />
                  <Button type="button" variant="ghost" size="icon" className="text-red-400 h-7 w-7" onClick={() => setLinks(links.filter((_, j) => j !== i))} data-testid={`link-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input value={l.anchor || ""} onChange={(e) => upd(i, { anchor: e.target.value })} placeholder="Anchor text" className="bg-black/30 border-white/15 text-sm" data-testid={`link-anchor-${i}`} />
                <select value={l.location || "related_links"} onChange={(e) => upd(i, { location: e.target.value })} className="cf-select bg-black/30 border border-white/15 rounded-md px-2 py-2 text-sm" data-testid={`link-location-${i}`}>
                  {LOCATIONS.map((loc) => <option key={loc.value} value={loc.value}>{loc.label}</option>)}
                </select>
              </div>
              {res && res.status !== "ok" && <p className="text-[11px] text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> This target is {res.status}. Replace or disable this link — it will not render publicly.</p>}
            </div>
          );
        })}
        {links.length === 0 && <p className="text-white/40 text-xs">No internal links yet. Add some above or accept a suggestion below.</p>}
      </div>

      {/* Suggestions */}
      {entityId ? (
        <div className="rounded-lg border border-gold/20 bg-gold/5 p-3" data-testid="link-suggestions">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gold flex items-center gap-2"><Sparkles className="h-4 w-4" /> Suggested Links</p>
            <div className="flex gap-2">
              {dismissed.length > 0 && <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowDismissed((v) => !v)} data-testid="link-toggle-dismissed">{showDismissed ? "Hide" : `Dismissed (${dismissed.length})`}</Button>}
              <Button type="button" variant="outline" size="sm" className="h-7 gap-1" onClick={() => loadSuggestions(true)} disabled={loadingSug} data-testid="link-refresh-suggestions">{loadingSug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh</Button>
            </div>
          </div>
          {showDismissed && (
            <div className="mb-3 space-y-1">
              {dismissed.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs text-white/60">
                  <span>{d.target_type}: {d.target_id}</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 text-gold" onClick={() => restore(d)}><RotateCcw className="h-3 w-3" /> Restore</Button>
                </div>
              ))}
            </div>
          )}
          {suggestions === null ? <p className="text-xs text-white/40">Loading…</p> :
            suggestions.length === 0 ? <p className="text-xs text-white/40" data-testid="link-suggestions-empty">No suggestions right now. Add focus keywords / categories to improve matching.</p> :
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <div key={`${s.target_type}-${s.target_id}`} className="rounded-md border border-white/10 bg-black/20 p-2.5 flex items-center justify-between gap-2 flex-wrap" data-testid={`suggestion-${s.target_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white/85 truncate">{s.name} <span className="text-white/40 text-[11px]">· {s.target_type}</span></p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(s.reasons || []).map((r, i) => <span key={i} className="text-[10px] rounded-full bg-white/10 text-white/60 px-2 py-0.5">{r}</span>)}
                        {s.stock_status === "out_of_stock" && <span className="text-[10px] rounded-full bg-red-500/15 text-red-300 px-2 py-0.5">out of stock</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button type="button" size="sm" className="cf-gold-btn h-7 gap-1" onClick={() => acceptSuggestion(s)} data-testid={`suggestion-accept-${s.target_id}`}><Check className="h-3.5 w-3.5" /> Add</Button>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-white/50" title="Reject" onClick={() => actSuggestion(s, "reject")}><X className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-white/40" title="Dismiss permanently" onClick={() => actSuggestion(s, "dismiss")} data-testid={`suggestion-dismiss-${s.target_id}`}><EyeOff className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>}
        </div>
      ) : (
        <p className="text-xs text-white/40">Save this item first to see intelligent link suggestions.</p>
      )}
    </div>
  );
}
